import React from "react";

import { motion, AnimatePresence } from "framer-motion";
import Button from "../Buttons/Button";

const DrawerAdv = ({
  children,
  isOpen,
  onClose,
  position = "right", // right, left, top, bottom
  width = "w-94",
  height = "h-94",
  title,
  showCloseButton = true,
  closeOnOutsideClick = false,
  zIndex = 52, // Added zIndex prop
  bodyGap = true,
}) => {
  const handleBackdropClick = (e) => {
    if (closeOnOutsideClick) {
      onClose();
    }
  };

  // Determine position-specific styles
  const getPositionStyles = () => {
    switch (position) {
      case "left":
        return {
          drawerClass: `left-0 top-0 h-full`,
          dimensionClass: width,
          drawerContentClass: `w-[calc(100%-28px)] left-0 h-full `,
          closeButtonClass: "!rounded-l-none !rounded-r",
          buttonWrapperClass: "top-0 right-0 z-[155] absolute",
        };
      case "top":
        return {
          drawerClass: `left-0 top-0 w-full`,
          drawerContentClass: `h-[calc(100%-28px)] bottom-0 w-full`,
          dimensionClass: height,
          closeButtonClass: "!rounded-t !rounded-b-none",
          buttonWrapperClass: "top-0 right-0 z-[155] absolute",
        };
      case "bottom":
        return {
          drawerClass: `left-0 bottom-0 w-full`,
          drawerContentClass: `h-[calc(100%-28px)] bottom-0 w-full`,
          dimensionClass: height,
          closeButtonClass: "!rounded-b-none !rounded-t",
          buttonWrapperClass: "top-0 right-0 z-[155] absolute",
        };
      case "right":
      default:
        return {
          drawerClass: `right-0 top-0 h-full`,
          drawerContentClass: `w-[calc(100%-28px)] right-0 h-full`,
          dimensionClass: width,
          closeButtonClass: "!rounded-r-none !rounded-l",
          buttonWrapperClass: "",
        };
    }
  };

  const {
    drawerClass,
    drawerContentClass,
    dimensionClass,
    closeButtonClass,
    buttonWrapperClass,
  } = getPositionStyles();

  // Animation variants based on position
  const getAnimationVariants = () => {
    switch (position) {
      case "left":
        return {
          initial: { x: "-100%" },
          animate: { x: 0 },
          exit: { x: "-100%" },
        };
      case "top":
        return {
          initial: { y: "-100%" },
          animate: { y: 0 },
          exit: { y: "-100%" },
        };
      case "bottom":
        return {
          initial: { y: "100%" },
          animate: { y: 0 },
          exit: { y: "100%" },
        };
      case "right":
      default:
        return {
          initial: { x: "100%" },
          animate: { x: 0 },
          exit: { x: "100%" },
        };
    }
  };

  const { initial, animate, exit } = getAnimationVariants();

  // Custom transition with smooth ease-in-out
  const smoothTransition = {
    duration: 0.5,
    ease: [0.4, 0, 0.2, 1], // Custom cubic-bezier for smooth motion
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 overflow-hidden" style={{ zIndex }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleBackdropClick}
            className={`fixed inset-0 bg-[#000000]/70 transition-opacity z-[53]`}
          />
          <motion.div
            className={`drawerWrapper ${dimensionClass} flex flex-1 z-[55] absolute ${drawerClass}`}
            initial={initial}
            animate={animate}
            exit={exit}
            transition={smoothTransition}
          >
            <div className={buttonWrapperClass} onClick={handleBackdropClick}>
              {showCloseButton && (
                <Button
                  type="icon"
                  icon={"close"}
                  onClick={onClose}
                  size="sm"
                  className={closeButtonClass}
                />
              )}
            </div>
            <div
              className={`${drawerContentClass} bg-white absolute flex overflow-hidden`}
            >
              <div className="flex flex-1 flex-col gap-3 overflow-hidden py-3">
                <div className="px-3">
                  <span className="text-xl font-semibold text-gray-900">
                    {title}
                  </span>
                </div>
                <div className="overflow-auto px-3 flex flex-1">{children}</div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DrawerAdv;
